export const getOptimizedImageUrl = (image, options = {}) => {
    const baseUrl = image?.fields?.file?.url;
    if (!baseUrl) return '';

    const url = baseUrl.startsWith('http') ? baseUrl : `https:${baseUrl}`;

    const params = new URLSearchParams();

    // Determine if we're on mobile based on screen width
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const config = isMobile ? (options.mobile || {}) : (options.desktop || {});

    if (config.width) params.append('w', config.width);
    if (config.height) params.append('h', config.height);
    if (config.quality) params.append('q', config.quality);
    if (config.fit) params.append('fit', config.fit);
    params.append('fm', 'webp'); // Use WebP format for better compression

    return `${url}?${params.toString()}`;
};

export const getThumbnailUrl = (image, options = {}) => {
    // Get original dimensions to determine orientation
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    let thumbnailConfig;

    if (originalWidth && originalHeight) {
        const isPortrait = originalHeight > originalWidth;
        const isLandscape = originalWidth > originalHeight;

        if (isPortrait) {
            // For portraits: constrain by height
            thumbnailConfig = {
                mobile: {
                    height: 400,
                    quality: 95,
                    fit: 'fill'
                },
                desktop: {
                    height: 400,
                    quality: 95,
                    fit: 'fill'
                }
            };
        } else if (isLandscape) {
            // For landscapes: constrain by width
            thumbnailConfig = {
                mobile: {
                    width: 200,
                    quality: 95,
                    fit: 'fill'
                },
                desktop: {
                    width: 400,
                    quality: 95,
                    fit: 'fill'
                }
            };
        } else {
            // For square images: use height constraint
            thumbnailConfig = {
                mobile: {
                    height: 200,
                    quality: 95,
                    fit: 'fill'
                },
                desktop: {
                    height: 400,
                    quality: 95,
                    fit: 'fill'
                }
            };
        }
    } else {
        // Fallback to original config
        thumbnailConfig = {
            mobile: {
                height: 200,
                quality: 85,
                fit: 'fill'
            },
            desktop: {
                height: 400,
                quality: 85,
                fit: 'fill'
            }
        };
    }

    return getOptimizedImageUrl(image, { ...thumbnailConfig, ...options });
};

// Returns a DPR-aware optimized URL for either a Contentful asset object or a direct URL string.
export const getDprImageUrl = (source, baseMax = 750, opts = {}) => {
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const maxPx = Math.ceil(baseMax * Math.min(dpr, 3));

    // If it's a Contentful asset object with dimensions, pick width/height based on orientation
    const fileUrl = typeof source === 'string'
        ? source
        : source?.fields?.file?.url;

    if (!fileUrl) return '';

    const url = fileUrl.startsWith('http') ? fileUrl : `https:${fileUrl}`;

    const params = new URLSearchParams();
    const originalWidth = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.width;
    const originalHeight = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.height;

    if (originalWidth && originalHeight) {
        if (originalWidth >= originalHeight) {
            params.set('w', String(Math.min(originalWidth, maxPx)));
        } else {
            params.set('h', String(Math.min(originalHeight, maxPx)));
        }
    } else {
        // Fallback when dimensions are unknown: constrain by width
        params.set('w', String(maxPx));
    }

    params.set('fm', opts.format || 'webp');
    params.set('q', String(opts.quality || 85));
    if (opts.fit) params.set('fit', opts.fit);

    // Preserve existing query parameters if any
    const hasQuery = url.includes('?');
    if (hasQuery) {
        const [base, existing] = url.split('?');
        const existingParams = new URLSearchParams(existing);
        // Do not overwrite explicit existing sizing params
        for (const [k, v] of existingParams.entries()) {
            if (!params.has(k)) params.set(k, v);
        }
        return `${base}?${params.toString()}`;
    }
    return `${url}?${params.toString()}`;
};
